// #3008 judge round 1 (B1), driver decision rule 2 -- a tiny in-memory
// pub/sub so an OPEN useScoreSheet hook can re-seat its in-memory sheet when
// that sheet's IndexedDB content is REPLACED out from under it: either by
// db.js's bulkPutAncestorAware writing a server row through (rule 1's safe,
// no-divergence branch), or by sync.js's applyOp landing a successful
// updateScoreSheet -- which is also where a merge-resubmit's merged body
// lands, once threeWayMerge.js's disjoint-field auto-merge (rule 1's sibling
// rule) resolves a stale_write 409 with no human involvement.
//
// Framework-agnostic on purpose, same shape as unsavedEdits.js's registry
// right next to it: db.js/sync.js have no hook to push React state into, and
// this needs to fire synchronously from inside their own write paths. Never
// persists across a reload -- a reload re-assembles the hook's state from
// scratch anyway, so there is nothing to replay.
//
// Keyed by the SAME entityKey format entityKeyOf() computes in db.js
// (`${rideId}::${judgeId}`, no `scoreSheet:` prefix) and unsavedEdits.js's
// registry, so a subscriber only ever hears about its OWN sheet.
const listenersByKey = new Map();

// #3008 round 6, driver decision candidate 2 -- `generation` is an OPTIONAL
// 3rd arg, forwarded to listeners as their 2nd callback param. sync.js's
// applyOp("updateScoreSheet") success (ordinary save or merge-resubmit
// alike) passes the op's own stamped generation here; db.js's
// bulkPutAncestorAware server-replacement call site passes none (stays
// `undefined`), unchanged from before this round. useScoreSheet.js's
// subscriber tells the two apart by whether `generation` is `undefined`.
export function emitSheetContentReplaced(entityKey, content, generation) {
  if (!entityKey) return;
  const listeners = listenersByKey.get(entityKey);
  if (!listeners || listeners.size === 0) return;
  // Iterate a copy: a listener unsubscribing itself mid-callback (a hook
  // unmounting in response, say) must not skip a sibling listener by
  // mutating the Set this loop is walking.
  for (const listener of [...listeners]) listener(content, generation);
}

// Returns an unsubscribe function, the same shape a React effect cleanup
// expects (`useEffect(() => subscribeToSheetContentReplaced(...), [...])`).
export function subscribeToSheetContentReplaced(entityKey, listener) {
  if (!entityKey) return () => {};
  let listeners = listenersByKey.get(entityKey);
  if (!listeners) {
    listeners = new Set();
    listenersByKey.set(entityKey, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) listenersByKey.delete(entityKey);
  };
}

// Test-only: reset between specs so one test's subscribers/emits can't leak
// into another's assertions (this registry is module-level).
export function _resetSheetContentEventsForTests() {
  listenersByKey.clear();
}
